/**
 * Browser-local timestamp formatting.
 *
 * The backend serialises every timestamp as a timezone-aware ISO-8601
 * string (UTC ``Z`` or an explicit offset). The UI used to render those
 * raw (``2026-06-04 14:02Z``), which operators in AEST mis-read as local
 * time. ``formatLocal`` converts to the *browser's* local zone and tags
 * the zone abbreviation so it's unambiguous: ``2026-06-04 14:02 AEST``.
 *
 * ``<LocalTime>`` wraps that in a ``<span>`` whose ``title`` is the raw
 * (UTC) ISO, so the original value is one hover away.
 */
import * as React from "react";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** The short timezone-name part for the browser's locale + zone, e.g.
 *  ``AEST`` / ``GMT+10`` depending on the browser. Empty on failure. */
function tzAbbr(d: Date): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: "short",
    }).formatToParts(d);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * Format an ISO timestamp in the browser's local timezone as
 * ``YYYY-MM-DD HH:MM[:SS] <TZ>``. ``null`` / ``undefined`` / empty →
 * ``"—"``; an unparseable value is returned verbatim.
 */
export function formatLocal(
  iso: string | null | undefined,
  opts?: { seconds?: boolean },
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time =
    `${pad(d.getHours())}:${pad(d.getMinutes())}` +
    (opts?.seconds ? `:${pad(d.getSeconds())}` : "");
  const tz = tzAbbr(d);
  return tz ? `${date} ${time} ${tz}` : `${date} ${time}`;
}

/**
 * Compact local-time label for chart axes: ``MM-DD HH:MM`` in the
 * browser's local zone (no year, no tz tag — kept short to fit a tick).
 * Replaces the old ``iso.slice(5, 16).replace("T", " ")`` axis hack,
 * which left the value in UTC.
 */
export function formatLocalShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/**
 * Local time-of-day only (``HH:MM:SS``) — for transcript / log lines
 * where the date is implied by context. Replaces the old
 * ``ts.split("T")[1].replace(/\..*$/,"Z")`` hack (which showed UTC).
 * Unparseable input is returned verbatim.
 */
export function formatLocalTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Render a local-time timestamp with the raw (UTC) ISO available on
 * hover. Drop-in for the old ``{ts.replace("T"," ").replace(/\..*$/,"Z")}``
 * pattern: ``<LocalTime iso={ts} />``.
 */
export function LocalTime({
  iso,
  seconds,
  className,
}: {
  iso: string | null | undefined;
  seconds?: boolean;
  className?: string;
}): React.ReactElement {
  if (!iso) return <span className={className}>—</span>;
  return (
    <span className={className} title={iso}>
      {formatLocal(iso, { seconds })}
    </span>
  );
}
